import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Zap, Download, LogOut, Plus, Clock } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS = {
  awaiting_payment: ["Awaiting payment", "bg-slate-500/20 text-slate-200"],
  paid: ["In line", "bg-[#3b4049]/25 text-[#e6e8ec]"],
  in_progress: ["AI is editing", "bg-amber-400/15 text-amber-200"],
  delivered: ["Ready", "bg-emerald-400/15 text-emerald-200"],
  cancelled: ["Cancelled", "bg-red-500/15 text-red-200"],
};

const TOOL_NAME = { person: "Person Swap", generate: "Image Generator", "video-background": "Video Swap", "video-person": "Video Swap" };

const OrderCard = ({ o }) => {
  const original = `${API}/my/orders/${o.id}/original`;
  const result = `${API}/my/orders/${o.id}/result`;
  const [label, cls] = STATUS[o.status] || STATUS.paid;
  const working = o.payment_status === "paid" && !o.has_result && o.status !== "cancelled";
  return (
    <div data-testid={`my-order-${o.id}`} className="glass rounded-[28px] overflow-hidden">
      <div className="relative aspect-[4/3] sm:aspect-[16/9] bg-black">
        {o.has_result && o.mode?.startsWith("video") ? (
          <video src={result} controls loop muted playsInline className="absolute inset-0 h-full w-full object-cover" data-testid={`my-order-video-${o.id}`} />
        ) : o.has_result && o.mode === "generate" ? (
          <img src={result} alt="Generated scene" className="absolute inset-0 h-full w-full object-cover" data-testid={`my-order-result-${o.id}`} />
        ) : o.has_result ? (
          <BeforeAfter before={original} after={result} alt="Your photo" testId={`my-order-slider-${o.id}`} className="absolute inset-0" />
        ) : (
          <>
            {o.mode?.startsWith("video") ? <div className="absolute inset-0 bg-[#0b0b0d]" /> : <img src={original} alt="Your upload" className="absolute inset-0 h-full w-full object-cover opacity-70" />}
            {working && (
              <div className="absolute inset-0 grid place-items-center bg-black/40">
                <div className="flex items-center gap-3 glass rounded-full px-5 py-3 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-[#d5d9df]" /> {o.ai_status === "failed" ? "Taking a little longer, we're on it" : "Creating your new scene…"}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="p-5 sm:p-6 flex flex-wrap items-center gap-3">
        <span data-testid="my-order-status" className={`text-xs px-2.5 py-1 rounded-full ${cls}`}>{label}</span>
        {o.rush && <span className="text-xs px-2.5 py-1 rounded-full bg-[#3b4049] flex items-center gap-1"><Zap className="h-3 w-3" /> Rush</span>}
        <span className="font-display font-semibold">{TOOL_NAME[o.mode] ? `${TOOL_NAME[o.mode]} · ` : ""}{o.style === "custom" && o.custom_request ? o.custom_request.slice(0, 40) : o.style_name}</span>
        <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(o.created_at).toLocaleString()}</span>
        {o.has_result && (
          <a href={result} download data-testid={`my-order-download-${o.id}`} className="btn-primary ml-auto px-5 py-2.5 text-sm">
            <Download className="h-4 w-4" /> Download
          </a>
        )}
      </div>
    </div>
  );
};

export default function MyOrders() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    if (user === false) nav("/login");
    if (user) load();
  }, [user, load, nav]);

  const pending = (orders || []).some((o) => o.payment_status === "paid" && !o.has_result);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [pending, load]);

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="my-orders-page">
      <header className="border-b border-white/10 px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="/" className="flex items-center gap-3"><LogoMark className="h-8 w-8" /><span className="font-display font-bold">My Orders</span></a>
        <button data-testid="my-orders-logout" onClick={async () => { await logout(); nav("/"); }} className="btn-ghost px-4 py-2 text-sm"><LogOut className="h-4 w-4" /> Log out</button>
      </header>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-mono mb-2">Signed in as {user?.email} · <span data-testid="my-orders-credits">{user?.unlimited ? "Unlimited credits" : `${user?.credits ?? 0} credits${(user?.free_left ?? 0) > 0 ? ` · ${user.free_left} free creations left` : ""}`}</span></div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">Your swaps</h1>
          </div>
          <Link to="/#order" data-testid="my-orders-new" className="btn-primary px-5 py-3 text-sm"><Plus className="h-4 w-4" /> New swap</Link>
        </div>
        <div className="grid gap-6" data-testid="my-orders-list">
          {orders === null && <p className="text-slate-400">Loading…</p>}
          {orders?.length === 0 && <p className="text-slate-400" data-testid="my-orders-empty">No orders yet. Upload a photo to start your first shift.</p>}
          {(orders || []).map((o) => <OrderCard key={o.id} o={o} />)}
        </div>
      </div>
    </main>
  );
}
